(() => {
  if (customElements.get('croma-cart-discounts')) return;
  // Serialize mutations so note/quantity changes cannot overwrite a discount response.
  let pending = Promise.resolve();
  const request = async (path, body) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };
  window.CromaCart = {
    run(task) {
      const result = pending.then(async () => {
        const checkout = [...document.querySelectorAll('button[name="checkout"]')].map(button => ({ button, disabled: button.disabled }));
        checkout.forEach(({ button }) => { button.disabled = true; });
        try { return await task(request); }
        finally { checkout.forEach(({ button, disabled }) => { button.disabled = disabled; }); }
      });
      pending = result.catch(() => {});
      return result;
    },
    codes(cart) {
      if (Array.isArray(cart.discount_codes)) return cart.discount_codes.filter(entry => entry.applicable).map(entry => entry.code);
      const applications = [...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application))];
      return [...new Set(applications.filter(entry => entry?.type === 'discount_code').map(entry => entry.title))];
    }
  };
  class CartDiscounts extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-discount-input]');
      this.status = this.querySelector('[data-discount-status]');
      this.codesList = this.querySelector('[data-discount-codes]');
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.classList.add('is-ready');
      if (!this.closest('croma-cart-drawer')) {
        window.CromaCart.run(request => request('cart.js')).then(cart => {
          if (this.isConnected) this.renderCodes(cart);
        }).catch(() => {});
      }
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }
    onClick = event => {
      if (event.target.closest('[data-discount-apply]')) this.update();
      const remove = event.target.closest('[data-discount-remove]');
      if (remove) this.update(remove.dataset.discountRemove);
    };
    onKeydown = event => {
      if (event.target === this.input && event.key === 'Enter') {
        event.preventDefault();
        this.update();
      }
    };
    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.input.readOnly = busy;
      this.querySelectorAll('button').forEach(button => button.setAttribute('aria-disabled', String(busy)));
    }
    async update(removeCode) {
      if (this.busy) return;
      const code = this.input.value.trim();
      if (!removeCode && (!code || code.includes(','))) {
        this.status.textContent = this.dataset.invalidLabel;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        return;
      }
      this.busy = true;
      this.setBusy(true);
      this.status.textContent = '';
      this.input.removeAttribute('aria-invalid');
      try {
        await window.CromaCart.run(async request => {
          if (!this.isConnected) return;
          const before = await request('cart.js');
          const codes = window.CromaCart.codes(before);
          const normalized = value => value.toLowerCase();
          const requested = removeCode
            ? codes.filter(value => normalized(value) !== normalized(removeCode))
            : [...codes.filter(value => normalized(value) !== normalized(code)), code];
          const pageControl = document.querySelector('croma-cart-discounts[data-section-id]');
          const sectionId = pageControl?.dataset.sectionId;
          const note = this.closest('croma-cart-drawer')?.querySelector('[data-cart-note-input]') || this.closest('form')?.querySelector('[name="note"]');
          const cart = await request('cart/update.js', {
            discount: requested.join(','),
            ...(note ? { note: note.value } : {}),
            ...(sectionId ? { sections: [sectionId], sections_url: `${window.Shopify?.routes?.root || '/'}cart` } : {})
          });
          document.querySelector('croma-cart-drawer')?.render(cart);
          document.querySelectorAll('croma-cart-discounts').forEach(element => {
            if (!element.closest('croma-cart-drawer')) element.renderCodes(cart);
          });
          if (sectionId) await this.refreshPage(cart, sectionId);
          if (!this.isConnected) return;
          const applicable = window.CromaCart.codes(cart).some(value => normalized(value) === normalized(code));
          const allocations = [...(cart.cart_level_discount_applications || []),
            ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application))];
          const reflected = allocations.some(entry => entry?.type === 'discount_code' && normalized(entry.title) === normalized(code));
          this.status.textContent = removeCode ? this.dataset.removedLabel : !applicable ? this.dataset.invalidLabel
            : reflected ? this.dataset.appliedLabel : this.dataset.shippingLabel || this.dataset.appliedLabel;
          if (!removeCode && !applicable) this.input.setAttribute('aria-invalid', 'true');
          else this.input.value = '';
          // Removing a focused code replaces its button; return focus to the input.
          if (removeCode) this.input.focus({ preventScroll: true });
        });
      } catch (error) {
        if (this.isConnected) this.status.textContent = this.dataset.errorLabel;
      } finally {
        this.busy = false;
        if (this.isConnected) this.setBusy(false);
      }
    }
    renderCodes(cart) {
      this.status.textContent = '';
      this.codesList.replaceChildren();
      window.CromaCart.codes(cart).forEach(code => {
        const item = document.createElement('li');
        const text = document.createElement('span');
        text.textContent = code;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'croma-cart-discounts__remove';
        remove.dataset.discountRemove = code;
        remove.setAttribute('aria-label', this.dataset.removeLabel.replace('[code]', code));
        remove.setAttribute('aria-disabled', String(Boolean(this.busy)));
        remove.textContent = '\u00d7';
        item.append(text, remove);
        this.codesList.append(item);
      });
    }
    async refreshPage(cart, sectionId) {
      let html = cart.sections?.[sectionId];
      if (!html) {
        const url = new URL(`${window.Shopify?.routes?.root || '/'}cart`, location.origin);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Cart section request failed');
        html = await response.text();
      }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const root = document.getElementById(`shopify-section-${sectionId}`);
      if (!root) return;
      if (Number(cart.item_count) === 0) {
        root.innerHTML = parsed.getElementById(`shopify-section-${sectionId}`)?.innerHTML || parsed.body.innerHTML;
        return;
      }
      // Preserve notes, expanded state, typed code and focus: replace only server prices/items.
      const updates = ['.croma-main-cart__items', '.croma-main-cart__totals', '.croma-main-cart__count'].map(selector => {
        const current = root.querySelector(selector);
        const updated = parsed.querySelector(selector);
        if (!current || !updated) throw new Error('Incomplete cart section');
        return { current, updated };
      });
      const focused = document.activeElement;
      const pendingQuantities = [...root.querySelectorAll('input[data-cart-line-key]')]
        .filter(input => input.value !== input.defaultValue)
        .map(input => ({ key: input.dataset.cartLineKey, value: input.value }));
      const focusedKey = focused?.matches('input[data-cart-line-key]') ? focused.dataset.cartLineKey : null;
      updates.forEach(({ current, updated }) => current.replaceWith(updated));
      root.querySelectorAll('input[data-cart-line-key]').forEach(input => {
        const pending = pendingQuantities.find(entry => entry.key === input.dataset.cartLineKey);
        if (pending) input.value = pending.value;
        if (focusedKey === input.dataset.cartLineKey) input.focus({ preventScroll: true });
      });
    }
  }
  customElements.define('croma-cart-discounts', CartDiscounts);
})();
